"use client";

import React from "react";
import SubPageLayout from "@/components/common/SubPageLayout";
import DiagramSection from "@/components/diagrams/DiagramSection";
import DiagramCard from "@/components/diagrams/DiagramCard";
import PlainDutchDiagram from "@/components/diagrams/PlainDutchDiagram";
import TwillDutchDiagram from "@/components/diagrams/TwillDutchDiagram";
import ReverseDutchDiagram from "@/components/diagrams/ReverseDutchDiagram";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COPY } from "./content";
const diagramCopy = {
  sectionTitle: {
    en: "Dutch Weave Structures & Micro-Pore Mechanics",
    zh: "荷兰编织微观结构与微孔力学原理",
    es: "Estructuras de Tejido Dutch y Mecánica de Microporos",
    de: "Tressengewebe-Strukturen und Mikroporen-Mechanik",
    fr: "Structures de Tissage Reps et Mécanique des Micropores",
    pt: "Estruturas de Trama Holandesa e Mecânica de Microporos"
  },
  sectionLead: {
    en: "Schematic representation of the three primary stainless steel dutch weaves, highlighting wire interlacing, pore geometry, and tortuous filtration paths.",
    zh: "三大主要不锈钢荷兰编织结构原理图，展示金属丝交织方式、立体微孔流道与曲折拦截路径。",
    es: "Representación esquemática de los tres tejidos Dutch principales, destacando el entrelazado de alambres y la geometría tortuosa de los poros.",
    de: "Schematische Darstellung der drei primären Tressengewebe mit Kett- und Schussanordnung, Porengeometrie und gewundenem Strömungsverlauf.",
    fr: "Représentation schématique des trois tissages reps principaux illustrant l'entrecroisement des fils, la géométrie des pores et les trajets de filtration.",
    pt: "Representação esquemática dos três tecidos holandeses primários, destacando o entrelaçamento de fios e a geometria tortuosa dos poros."
  },
  plainTitle: {
    en: "Plain Dutch Weave",
    zh: "平纹荷兰编织",
    es: "Tejido Dutch Liso",
    de: "Glatte Tresse (Plain Dutch)",
    fr: "Tissage Reps Uni",
    pt: "Trama Holandesa Lisa"
  },
  plainDesc: {
    en: "1/1 plain weave with heavy warp and tightly packed fine weft wires. Triangular micro-openings provide reliable surface filtration and easy cake discharge.",
    zh: "1/1 平纹走线，较粗间距经丝与密排细纬丝交织，形成规则三角形微孔，兼顾截留与滤饼脱离。",
    es: "Ligamento liso 1/1 con urdimbre pesada y trama fina compacta. Poros triangulares micrométricos para filtración superficial predecible.",
    de: "1/1 Bindung mit starken Kett- und dicht geschlagenen Schussdrähten für dreieckige Mikroporen und leichten Kuchenabwurf.",
    fr: "Armure unie 1/1 à chaîne forte et trame fine serrée. Pores triangulaires réguliers assurant une rétention stable.",
    pt: "Tecedura lisa 1/1 com urdume robusto e trama fina compacta gerando microporos triangulares uniformes."
  },
  twillTitle: {
    en: "Twill Dutch Weave",
    zh: "斜纹荷兰编织",
    es: "Twill Dutch Weave",
    de: "Köpertresse (Twill Dutch)",
    fr: "Toile Twill Dutch",
    pt: "Trama Holandesa Sarjada"
  },
  twillDesc: {
    en: "2/2 twill weave allowing double the weft density. Microscopic curved channels deliver micro-retention down to 1 µm under high differential pressure.",
    zh: "2/2 斜纹走线允许双倍纬丝密度紧密压实，曲折立体通道在严苛高压差下实现低至 1 微米微粒截留。",
    es: "Ligamento en sarga 2/2 que duplica la densidad de trama. Canales microscópicos curvos con retención de hasta 1 µm bajo alta presión.",
    de: "2/2 Köperbindung ermöglicht doppelte Schussdichte. Gewundene Mikropfade bieten Filtration ab 1 µm unter hohem Differenzdruck.",
    fr: "Armure sergée 2/2 doublant la densité de trame. Canaux micrométriques courbes offrant une rétention dès 1 µm sous forte pression.",
    pt: "Tecedura em sarja 2/2 que dobra a densidade da trama. Canais microscópicos curvos garantem retenção a partir de 1 µm sob alta pressão."
  },
  reverseTitle: {
    en: "Reverse Dutch Weave",
    zh: "反向荷兰编织",
    es: "Reverse Dutch Weave",
    de: "Panzertresse (Reverse Dutch)",
    fr: "Toile Reps Inversé",
    pt: "Trama Holandesa Reversa"
  },
  reverseDesc: {
    en: "Inverted wire arrangement with dense fine warp and heavy spaced weft wires, yielding extreme longitudinal tensile strength for continuous screen belts.",
    zh: "反向线径配比，紧排细经丝与高强度粗纬丝搭配，提供极高的纵向抗拉强度，专用于自动换网器连续滤带。",
    es: "Disposición inversa con urdimbre fina densa y trama pesada espaciada, proporcionando alta resistencia a tracción longitudinal.",
    de: "Umgekehrtes Drahtverhältnis mit dichter Feinkette und starken Schussdrähten für extreme Längszugfestigkeit in Siebbändern.",
    fr: "Ratio inversé à chaîne fine dense et trame robuste espacée, procurant une haute résistance longitudinale pour changeurs continus.",
    pt: "Proporção inversa com urdume fino denso e trama espessa espaçada, oferecendo grande resistência à tração longitudinal."
  }
};
export default function FilterClothPage() {
  const {
    lang
  } = useLanguage();
  const content = pick(lang, COPY);
  const diagram = <DiagramSection id="diagrams" title={pick(lang, diagramCopy.sectionTitle)} lead={pick(lang, diagramCopy.sectionLead)} tone="background">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <DiagramCard index="01" badge="ISO 9044 / ASTM E2814" title={pick(lang, diagramCopy.plainTitle)} description={pick(lang, diagramCopy.plainDesc)}>
          <PlainDutchDiagram />
        </DiagramCard>

        <DiagramCard index="02" badge="ISO 9044 / ASTM E2016" title={pick(lang, diagramCopy.twillTitle)} description={pick(lang, diagramCopy.twillDesc)}>
          <TwillDutchDiagram />
        </DiagramCard>

        <DiagramCard index="03" badge="ISO 9044 / ASTM E2016" title={pick(lang, diagramCopy.reverseTitle)} description={pick(lang, diagramCopy.reverseDesc)}>
          <ReverseDutchDiagram />
        </DiagramCard>
      </div>
    </DiagramSection>;
  return <SubPageLayout content={content} diagram={diagram} />;
}